<template>
  <div>
    <v-tooltip bottom>
      <template #activator="{ on }">
        <v-btn
          color="primary"
          icon
          small
          :loading="!item.uuid"
          :disabled="!canPerformAction('EDIT_APPLICATION')"
          v-on="on"
          @click="showDialog = true;"
        >
          <v-icon>
            mdi-account-switch
          </v-icon>
        </v-btn>
      </template>
      <div>
        <p
          class="
            mb-0
            caption font-weight-bold
          "
        >
          <span class="font-weight-medium">Assigner le candidat à une offre</span>
        </p>
      </div>
    </v-tooltip>

    <v-dialog
      :value="showDialog"
      persistent
      max-width="500"
    >
      <v-card>
        <v-card-title
          class="text-h5"
        >
          Associer candidat à une offre
        </v-card-title>
        <v-progress-linear
          v-if="isLoading"
          indeterminate
          color="primary"
        />
        <v-card-text>
          <v-form
            v-model="isValidForm"
          >
            <template
              v-for="(field, fieldIndex) in fields"
            >
              <div
                :key="fieldIndex"
              >
                <GenericField
                  v-if="field.name === 'entityUuid'"
                  v-model="copyItem.entityUuid"
                  :items="computedWorkspaces"
                  :field="{
                    ...field,
                    disabled: isLoading
                  }"
                  @input="copyItem.offerUuid = null;"
                />

                <PositionInput
                  v-else-if="field.name === 'offerUuid'"
                  v-model="copyItem[field.name]"
                  :field="{
                    ...field,
                    additionalFilters: [
                      {
                        match: {
                          'entity.name': getWorkspaceNameByUuid(copyItem.entityUuid)
                        }
                      },
                      {
                        match: {
                          status: 'published'
                        }
                      }
                    ],
                    disabled: isLoading
                  }"
                  :classes="getInputClasses(field)"
                  @item="item => offer = item"
                />
                <GenericField
                  v-else
                  v-model="copyItem.message"
                  :field="{
                    ...field,
                    disabled: isLoading
                  }"
                />
              </div>
            </template>
          </v-form>
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            @click="handleDisagree"
          >
            Annuler
          </v-btn>
          <v-btn
            :disabled="!isValidForm || isLoading"
            color="primary"
            text
            @click="handleAccept"
          >
            Accepter
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';

import moment from 'moment';

import { requests } from '@/mixins/requests';

import PositionInput from '@/components/forms/PositionInput.vue';
import GenericField from '@/components/dynamic/form/templates/GenericField.vue';

export default {
  name: 'CandidateAssignOffer',
  components: { PositionInput, GenericField },
  props: {
    item: {
      type: Object,
      default: () => ({})
    }
  },
  data () {
    return {
      copyItem: {},
      showDialog: false,
      offer: {},
      fields: [
        {
          name: 'entityUuid',
          label: 'Centre',
          viewType: 'Picker',
          itemText: 'name',
          itemValue: 'entityUuid',
          required: true,
          mandatory: true,
          clearable: true
        },
        {
          name: 'offerUuid',
          label: 'Offre',
          component: 'PositionInput',
          contentWtype: 'offer',
          computed: 'offer.uuid',
          computedLabel: 'offer.name',
          required: true,
          mandatory: true,
          viewType: 'Picker'
        },
        {
          name: 'message',
          label: 'Ajouter des notes',
          viewType: 'Textarea',
          icon: 'text-long',
          type: 'text'
        }
      ],
      isValidForm: false,
      isLoading: false
    };
  },
  computed: {
    ...mapState(['references', 'user', 'workspaces', 'activeWorkspace']),
    ...mapGetters(['canPerformAction']),
    computedWorkspaces () {
      return this.workspaces.filter(workspace => workspace.code !== 'spontaneous').map(workspace => ({
        ...workspace,
        name: workspace.name,
        code: workspace.code,
        entityUuid: workspace.settings.entityUuid
      }));
    }
  },
  watch: {
    item (value) {
      this.copyItem = this.iniatilizeData(value);
    }
  },
  mounted () {
    this.copyItem = this.iniatilizeData(this.item);
  },
  methods: {
    iniatilizeData (value) {
      value.entityUuid = this.getWorkspaceEntityByCode(this.activeWorkspace);

      return JSON.parse(JSON.stringify(value)) || {};
    },
    handleDisagree () {
      this.copyItem = this.iniatilizeData(this.item);
      this.showDialog = false;
    },
    generateUniqueId () {
      const timestamp = Date.now();
      const random = Math.random().toString(36).substring(2, 15);
      const uniqueId = `${timestamp}_${random}`;
      return uniqueId;
    },
    async handleAccept () {
      this.$emit('handle-loading', true);
      this.isLoading = true;

      const {
        entityUuid,
        experiences,
        formations,
        languages,
        attachments,
        message,
        offerUuid
      } = this.copyItem;

      const transformedNotes = [];

      if (message) {
        transformedNotes.push({
          id: `note_${this.generateUniqueId()}`,
          collaborator: this.user.uuid,
          text: message,
          createTime: `${moment().toISOString()}`
        });
      }

      await this.saveApplication({
        workspace: 'collaborator',
        status: 'draft',

        isSpontaneousApplication: false,
        positionUuid: this.offer.position.uuid,
        tradeUuid: this.offer.position.trade.uuid,
        entityUuid,
        offerUuid,
        notes: transformedNotes,

        collaborator: this.item.uuid,
        recruiter: this.user.uuid,
        recruiterStatus: 'TO_SORT',
        prospectStatus: 'IN_PROCESS',
        experiences,
        formations,
        languages,
        attachments,
        source: 'UCPA'
      }, false);

      this.showDialog = false;

      this.isLoading = false;
      this.$emit('handle-loading', false);
    },
    async saveApplication (input, isEditMode) {
      const response = await requests.saveApplication({
        input,
        isEditMode: isEditMode
      });

      if (!response.success) {
        this.$store.commit('setSnackbar', { text: 'Erreur lors de l\'enregistrement.', color: 'error' });
      } else {
        this.$store.commit('setSnackbar', { text: 'Enregistrer avec succès.', color: 'success' });
      }
    },
    getFieldReferences (optionsByReference) {
      if (!optionsByReference) return [];

      return Object.entries(this.references[optionsByReference]).map(([key, value]) => ({ text: value, value: key }));
    },
    getInputClasses (field) {
      const classes = [field.mandatory ? 'required' : ''];

      if (this.isViewMode) classes.push('input-color-black');

      return classes.join(' ');
    },
    getWorkspaceNameByUuid (entityUuid) {
      return this.computedWorkspaces.find(workspace => workspace.entityUuid === entityUuid)?.name;
    },
    getWorkspaceEntityByCode (entityCode) {
      return this.computedWorkspaces.find(workspace => workspace.code === entityCode)?.settings.entityUuid;
    }
  }
};
</script>
